import { ModelEntityType } from '@upright/common/model/input/types';
import { SdgDrillDownTableRow } from '@upright/common/model/output/scores/sdg-alignment/api-types';
import { SdgAlignmentValence, SdgId, SdgTarget } from '@upright/common/model/output/scores/sdg-alignment/types';
import { TableV2 } from '@upright/ui/common/generic/content/tables/table-v2';
import { ColumnV2 } from '@upright/ui/common/generic/content/tables/table-v2/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import {
  buildReasoningKey,
  ReasoningStateByKey,
  valenceToReasoningDirection,
} from '@upright/ui/common/upright/model/output/scores/sdg-alignments/reasonings/sdg-reasoning-keys';
import { LevelBox } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/level-box';
import { NameCell } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/name-cell';
import { AlignmentNumberCell } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/number-cell';
import { sortTableRows } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/sort';
import { TargetBox } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/target-box';
import { compact } from 'lodash';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

interface Props {
  sdg: SdgId;
  rows: SdgDrillDownTableRow[];
  selectedTarget: SdgTarget | undefined;
  selectedValence: SdgAlignmentValence;
  entityType: ModelEntityType;
  hasProductViewAccess: boolean;
  disablePaginationControls: boolean;
  reasoningStateByKey: ReasoningStateByKey;
}

export function SdgDrillDownModalTable(props: Props) {
  const isCompany = props.entityType === ModelEntityType.COMPANY;
  // Memoized so the `data` array reference stays stable across reasoning-poll
  // re-renders. TableV2 (TanStack) auto-resets the page index to 0 whenever the
  // `data` reference changes; without this, every ~3s reasoning poll — which
  // re-renders this component with a fresh `reasoningStateByKey` — would rebuild
  // the array and snap the user back to page 1 (PLT-4963). The reference now
  // only changes on genuine input changes (rows / target filter / entity type),
  // which legitimately should reset pagination.
  const sortedRows = useMemo(() => {
    const filteredRows = props.selectedTarget
      ? props.rows.filter(r => r.targets.find(d => d.target === props.selectedTarget))
      : props.rows;
    return sortTableRows(filteredRows, isCompany);
  }, [props.rows, props.selectedTarget, isCompany]);
  const alignedText = props.selectedValence === SdgAlignmentValence.ALIGNED ? 'Aligned' : 'Misaligned';
  const showLevelColumn = isCompany;

  const columns = useMemo(
    (): Array<ColumnV2<SdgDrillDownTableRow>> =>
      compact([
        {
          id: 'name',
          accessorKey: 'name',
          header: isCompany ? 'Product or service' : 'Constituent',
          cell: cellProps => {
            return (
              <NameCell
                valence={props.selectedValence}
                sdgId={props.sdg}
                data={cellProps.row.original}
                hasProductAccess={props.hasProductViewAccess}
              />
            );
          },
          sortingFn: 'text',
          filterFn: 'includesString',
          meta: { width: 'minmax(15em, auto)' },
        },
        {
          id: 'target',
          accessorKey: 'targets',
          header: `${alignedText} targets`,
          cell: cellProps => {
            const level = cellProps.row.original.strongestLevel;

            const productCode = cellProps.row.original.id;
            const direction = valenceToReasoningDirection(props.selectedValence);

            function getTargetsPart() {
              const targets = cellProps.row.original.targets;
              const allUnknown = targets.filter(d => d.target !== undefined).length === 0;
              if (allUnknown) {
                return <UnknownTargets>N/A</UnknownTargets>;
              } else {
                return cellProps.row.original.targets.map(d => {
                  const target = d.target;
                  if (target) {
                    const reasoningState = props.reasoningStateByKey.get(
                      buildReasoningKey(productCode, target, direction),
                    );
                    return <TargetBox key={target} target={target} level={d.level} reasoningState={reasoningState} />;
                  } else {
                    return <></>;
                  }
                });
              }
            }

            return (
              <TargetsCell>
                {getTargetsPart()}
                {showLevelColumn && <LevelBox level={level} sdgId={props.sdg} />}
              </TargetsCell>
            );
          },

          sortingFn: 'text',
          filterFn: 'includesString',
          meta: { align: 'right', width: 'minmax(max-content,auto)' },
        },
        !isCompany && {
          id: 'alignment',
          accessorKey: 'alignment',
          header: 'Alignment',
          cell: cellProps => {
            return <AlignmentNumberCell data={cellProps.row.original} />;
          },
          sortingFn: 'basic',
          filterFn: 'includesString',
          meta: { align: 'right', width: '6em' },
        },
      ]),
    [
      alignedText,
      isCompany,
      props.hasProductViewAccess,
      props.reasoningStateByKey,
      props.sdg,
      props.selectedValence,
      showLevelColumn,
    ],
  );

  return (
    <Root>
      <TableV2
        data={sortedRows}
        columns={columns}
        pageSize={10}
        isLoading={false}
        isError={false}
        disableSort={true}
        disablePaginationControls={props.disablePaginationControls}
        errorState={`Failed to load data`}
        showColumnSelect={false}
        tableAreaStyles={{
          front: css`
            min-height: 15em;
            max-height: none;
          `,
        }}
      />
    </Root>
  );
}

const Root = styled.div`
  min-height: 25em;
`;

const UnknownTargets = styled.span`
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
`;

const TargetsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 2px;
`;
